import React, { useEffect } from 'react';
import { apiCall } from '../config/api';
import { checkSpellingTolerance } from '../utils/fuzzyMatch';

const WordleGrid = () => {
  useEffect(() => {
    const initializeGame = async () => {
      // Game constants
      const ROWS = 6;
      const COLS = 5;
      
      // Game state
      let grid = Array(ROWS).fill().map(() => Array(COLS).fill(''));
      let currentRow = 0;
      let currentCol = 0;
      let gameOver = false;
      let isCheckingWord = false; // Prevent multiple simultaneous word checks
      let targetWord = '';
      let wordBank = [];
      let aminoleData = [];
      let gameMode = 'daily'; // 'daily' or 'freeplay'
      let dailyWordCompleted = false;
      let lastFreeplayWord = ''; // Track last word to avoid repeats
      let difficultyMode = 'normal'; // 'normal' or 'hard'
      
      // Question randomization state
      let questionHistory = []; // Track recent question types to avoid repetition
      const MAX_HISTORY_SIZE = 10; // Remember last 10 questions
      
      // Check if daily word was completed today (backend-powered)
      async function checkDailyWordStatus() {
        try {
          // Check if user is authenticated
          const token = localStorage.getItem('aminole_token');
          const userStr = localStorage.getItem('aminole_user');
          
          if (!token || !userStr) {
            // Not authenticated - use localStorage fallback
            const today = new Date().toDateString();
            const lastCompleted = localStorage.getItem('aminole_daily_completed');
            const lastCompletedDate = localStorage.getItem('aminole_daily_completed_date');
            
            if (lastCompleted && lastCompletedDate === today) {
              dailyWordCompleted = true;
              gameMode = 'freeplay';
            } else {
              dailyWordCompleted = false;
              gameMode = 'daily';
            }
            return;
          }

          const user = JSON.parse(userStr);
          if (user.role !== 'student') {
            // Not a student - use localStorage fallback
            dailyWordCompleted = false;
            gameMode = 'daily';
            return;
          }

          // Get daily word status from backend
          const response = await apiCall('/progress/daily-word-status');
          if (response.ok) {
            const data = await response.json();
            dailyWordCompleted = data.completedToday;
            gameMode = data.completedToday ? 'freeplay' : 'daily';
          } else {
            // Fallback to localStorage
            const today = new Date().toDateString();
            const lastCompleted = localStorage.getItem('aminole_daily_completed');
            const lastCompletedDate = localStorage.getItem('aminole_daily_completed_date');
            
            if (lastCompleted && lastCompletedDate === today) {
              dailyWordCompleted = true;
              gameMode = 'freeplay';
            } else {
              dailyWordCompleted = false;
              gameMode = 'daily';
            }
          }
        } catch (error) {
          // Fallback to localStorage on error
          const today = new Date().toDateString();
          const lastCompleted = localStorage.getItem('aminole_daily_completed');
          const lastCompletedDate = localStorage.getItem('aminole_daily_completed_date');
          
          if (lastCompleted && lastCompletedDate === today) {
            dailyWordCompleted = true;
            gameMode = 'freeplay';
          } else {
            dailyWordCompleted = false;
            gameMode = 'daily';
          }
        }
      }
      
      // Mark daily word as completed for today (backend-powered)
      async function markDailyWordCompleted() {
        try {
          // Check if user is authenticated
          const token = localStorage.getItem('aminole_token');
          const userStr = localStorage.getItem('aminole_user');
          
          if (!token || !userStr) {
            // Not authenticated - use localStorage fallback
            const today = new Date().toDateString();
            localStorage.setItem('aminole_daily_completed', 'true');
            localStorage.setItem('aminole_daily_completed_date', today);
            dailyWordCompleted = true;
            return;
          }

          const user = JSON.parse(userStr);
          if (user.role !== 'student') {
            // Not a student - use localStorage fallback
            const today = new Date().toDateString();
            localStorage.setItem('aminole_daily_completed', 'true');
            localStorage.setItem('aminole_daily_completed_date', today);
            dailyWordCompleted = true;
            return;
          }

          // Mark as completed on backend
          const response = await apiCall('/progress/daily-word-completed', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const data = await response.json();
            dailyWordCompleted = true;
            
            // Show streak information if available
            if (data.dailyWordStats && data.dailyWordStats.currentStreak > 1) {
              setTimeout(() => {
                alert(`Daily word completed! Current streak: ${data.dailyWordStats.currentStreak} days!`);
              }, 1500);
            }
          } else {
            // Fallback to localStorage
            const today = new Date().toDateString();
            localStorage.setItem('aminole_daily_completed', 'true');
            localStorage.setItem('aminole_daily_completed_date', today);
            dailyWordCompleted = true;
          }
        } catch (error) {
          // Fallback to localStorage on error
          const today = new Date().toDateString();
          localStorage.setItem('aminole_daily_completed', 'true');
          localStorage.setItem('aminole_daily_completed_date', today);
          dailyWordCompleted = true;
        }
      }
      
      // DOM elements
      const gridContainer = document.getElementById('wordle-grid');
      const keyboardContainer = document.getElementById('keyboard');
      const dailyModeBtn = document.getElementById('daily-mode');
      const freeplayModeBtn = document.getElementById('freeplay-mode');
      const questionTypeSelect = document.getElementById('question-type');
      const difficultySelect = document.getElementById('difficulty-mode');
      
      // Set up difficulty mode change handler
      if (difficultySelect) {
        // Function to update difficulty selector styling
        const updateDifficultyStyling = (value) => {
          const container = difficultySelect.closest('.difficulty-selector');
          if (container) {
            if (value === 'hard') {
              container.classList.add('difficulty-hard');
            } else {
              container.classList.remove('difficulty-hard');
            }
          }
        };
        
        difficultySelect.addEventListener('change', (e) => {
          difficultyMode = e.target.value;
          updateDifficultyStyling(e.target.value);
        });
        
        // Apply initial styling
        updateDifficultyStyling(difficultySelect.value);
      }

      // Get daily word based on date
      function getDailyWord() {
        const now = new Date();
        // Convert to EST
        const estOffset = -5 * 60; // EST is UTC-5
        const estTime = new Date(now.getTime() + (estOffset * 60000));
        
        // If before 9 AM EST, use previous day
        if (estTime.getHours() < 9) {
          estTime.setDate(estTime.getDate() - 1);
        }
        
        // Create seed from date
        const dateStr = estTime.toISOString().split('T')[0]; // YYYY-MM-DD
        let seed = 0;
        for (let i = 0; i < dateStr.length; i++) {
          seed = ((seed << 5) - seed + dateStr.charCodeAt(i)) & 0xffffffff;
        }
        
        return Math.abs(seed);
      }

      // Load word bank and set target word
      async function loadWordBank() {
        try {
          const response = await fetch(`${process.env.PUBLIC_URL}/assets/wordBank.json`);
          const data = await response.json();
          // Extract just the word strings from the objects
          wordBank = data.map(item => item.word.toLowerCase());
          
          // Load aminole data
          const aminoleResponse = await fetch(`${process.env.PUBLIC_URL}/assets/aminole_data_with_images.json`);
          const aminoleDataRaw = await aminoleResponse.json();
          aminoleData = aminoleDataRaw || [];
          
          if (wordBank.length > 0) {
            if (gameMode === 'daily') {
              const dailySeed = getDailyWord();
              targetWord = wordBank[dailySeed % wordBank.length].toUpperCase();
            } else {
              // Freeplay mode - ensure we get a different word than daily word AND last freeplay word
              const dailySeed = getDailyWord();
              const dailyWord = wordBank[dailySeed % wordBank.length].toUpperCase();
              
              let newWord;
              let attempts = 0;
              const maxAttempts = 50; // Prevent infinite loop
              
              do {
                newWord = wordBank[Math.floor(Math.random() * wordBank.length)].toUpperCase();
                attempts++;
              } while ((newWord === lastFreeplayWord || newWord === dailyWord) && attempts < maxAttempts && wordBank.length > 2);
              
              targetWord = newWord;
              lastFreeplayWord = targetWord; // Remember this word
            }
            // Target word set (not logged for security)
          }
        } catch (error) {
          console.error('Error loading word bank:', error);
          targetWord = 'AMINO'; // fallback
        }
      }

      // Reset game
      async function resetGame() {
        grid = Array(ROWS).fill().map(() => Array(COLS).fill(''));
        currentRow = 0;
        currentCol = 0;
        gameOver = false;
        
        // Hide mobile keyboard but keep the input element for reuse
        hideMobileInput();
        
        // Clear grid display
        const cells = document.querySelectorAll('.wordle-cell');
        cells.forEach(cell => {
          cell.textContent = '';
          cell.className = 'wordle-cell';
        });
        
        // Clear keyboard colors
        const keys = document.querySelectorAll('.keyboard-key');
        keys.forEach(key => {
          key.className = 'keyboard-key';
        });
        
        // Reload word bank with new target - await to ensure word is set
        await loadWordBank();
        console.log('Game reset complete. Mode:', gameMode, 'Target word set.');
      }

      // Create grid
      function createGrid() {
        gridContainer.innerHTML = '';
        
        for (let i = 0; i < ROWS; i++) {
          const row = document.createElement('div');
          row.className = 'wordle-row';
          
          for (let j = 0; j < COLS; j++) {
            const cell = document.createElement('div');
            cell.className = 'wordle-cell';
            cell.id = `cell-${i}-${j}`;
            cell.setAttribute('tabindex', '0');
            cell.setAttribute('role', 'button');
            cell.setAttribute('aria-label', `Letter ${j + 1} of guess ${i + 1}`);
            
            // Add touch/click handlers for mobile keyboard
            let touchHandled = false;
            
            cell.addEventListener('touchstart', (e) => {
              e.preventDefault();
              touchHandled = true;
              // Always focus the current game cell, not the tapped cell
              focusCurrentCell();
              if (isMobileDevice()) {
                showMobileInput();
              }
              // Reset flag after a short delay
              setTimeout(() => { touchHandled = false; }, 300);
            });
            
            cell.addEventListener('click', (e) => {
              // Prevent double handling on touch devices
              if (touchHandled) {
                e.preventDefault();
                return;
              }
              // Always focus the current game cell, not the clicked cell
              focusCurrentCell();
              // Trigger mobile keyboard on touch devices
              if (isMobileDevice()) {
                showMobileInput();
              }
            });
            
            row.appendChild(cell);
          }
          
          gridContainer.appendChild(row);
        }
      }

      // Create keyboard
      function createKeyboard() {
        const rows = [
          ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
          ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
          ['Enter', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'Backspace']
        ];
        
        keyboardContainer.innerHTML = '';
        keyboardContainer.className = 'keyboard';
        
        rows.forEach(row => {
          const keyboardRow = document.createElement('div');
          keyboardRow.className = 'keyboard-row';
          
          row.forEach(key => {
            const keyButton = document.createElement('button');
            keyButton.className = 'keyboard-key';
            keyButton.textContent = key;
            keyButton.addEventListener('click', () => handleKeyPress(key));
            keyboardRow.appendChild(keyButton);
          });
          
          keyboardContainer.appendChild(keyboardRow);
        });

      }

      // Mobile device detection
      function isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               ('ontouchstart' in window) ||
               (navigator.maxTouchPoints > 0);
      }
      
      // Global mobile input element - persistent for better mobile experience
      let currentMobileInput = null;
      let mobileInputInitialized = false;
      
      // Initialize persistent mobile input for touchscreen devices
      function initMobileInput() {
        if (mobileInputInitialized || !isMobileDevice()) return;
        mobileInputInitialized = true;
        
        // Create a persistent hidden input for mobile keyboard
        const mobileInput = document.createElement('input');
        mobileInput.type = 'text';
        mobileInput.id = 'mobile-keyboard-input';
        mobileInput.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 1px;
          opacity: 0;
          font-size: 16px;
          z-index: -1;
          pointer-events: none;
        `;
        mobileInput.setAttribute('autocomplete', 'off');
        mobileInput.setAttribute('autocorrect', 'off');
        mobileInput.setAttribute('autocapitalize', 'characters');
        mobileInput.setAttribute('spellcheck', 'false');
        mobileInput.setAttribute('inputmode', 'text');
        mobileInput.setAttribute('enterkeyhint', 'done');
        
        currentMobileInput = mobileInput;
        document.body.appendChild(mobileInput);
        
        // Handle text input
        mobileInput.addEventListener('input', (e) => {
          if (gameOver) return;
          
          const value = e.target.value.toUpperCase();
          if (value && value.length > 0) {
            // Process each new character
            const lastChar = value.slice(-1);
            if (/^[A-Z]$/.test(lastChar)) {
              handleKeyPress(lastChar);
            }
            // Clear input after processing
            mobileInput.value = '';
          }
        });
        
        // Handle special keys
        mobileInput.addEventListener('keydown', (e) => {
          if (gameOver) return;
          
          if (e.key === 'Backspace') {
            e.preventDefault();
            handleKeyPress('Backspace');
          } else if (e.key === 'Enter') {
            e.preventDefault();
            handleKeyPress('Enter');
          }
        });
        
        // Prevent blur from closing keyboard unexpectedly
        mobileInput.addEventListener('blur', () => {
          // Don't auto-close - let user tap elsewhere to close
        });
      }
      
      // Show mobile input and focus it to trigger keyboard
      function showMobileInput() {
        if (!currentMobileInput) {
          initMobileInput();
        }
        
        if (currentMobileInput && !gameOver) {
          // Clear any existing value
          currentMobileInput.value = '';
          
          // Focus to show keyboard
          currentMobileInput.style.pointerEvents = 'auto';
          currentMobileInput.focus();
          
          // On iOS, we need to use setSelectionRange to ensure cursor is at end
          setTimeout(() => {
            if (currentMobileInput) {
              currentMobileInput.setSelectionRange(0, 0);
            }
          }, 10);
        }
      }
      
      // Hide mobile keyboard
      function hideMobileInput() {
        if (currentMobileInput) {
          currentMobileInput.blur();
          currentMobileInput.style.pointerEvents = 'none';
        }
      }
      
      // Focus current cell
      function focusCurrentCell() {
        const currentCell = document.getElementById(`cell-${currentRow}-${currentCol}`);
        if (currentCell) {
          currentCell.focus();
        }
      }
      
      // Handle key press with duplicate prevention
      let lastKeyPress = { key: '', timestamp: 0 };
      
      function handleKeyPress(key) {
        if (gameOver) return;
        
        // Prevent duplicate key presses within 100ms
        const now = Date.now();
        if (lastKeyPress.key === key && (now - lastKeyPress.timestamp) < 100) {
          return;
        }
        lastKeyPress = { key, timestamp: now };

        if (key === 'Backspace') {
          // Only allow backspace on the current row being edited AND only if the current row hasn't been submitted yet
          if (currentCol > 0 && !isRowSubmitted(currentRow)) {
            currentCol--;
            grid[currentRow][currentCol] = '';
            updateDisplay();
          }
        } else if (key === 'Enter') {
          if (currentCol === COLS && !isCheckingWord) {
            checkWord();
          }
        } else if (key.length === 1 && currentCol < COLS && !isRowSubmitted(currentRow)) {
          grid[currentRow][currentCol] = key.toUpperCase();
          currentCol++;
          updateDisplay();
        }
      }

      // Check if a row has been submitted (has colored cells)
      function isRowSubmitted(rowIndex) {
        for (let j = 0; j < COLS; j++) {
          const cell = document.getElementById(`cell-${rowIndex}-${j}`);
          if (cell && (cell.classList.contains('correct') || cell.classList.contains('present') || 
                      cell.classList.contains('absent') || cell.classList.contains('incorrect'))) {
            return true;
          }
        }
        return false;
      }

      // Update display
      function updateDisplay() {
        for (let i = 0; i < ROWS; i++) {
          for (let j = 0; j < COLS; j++) {
            const cell = document.getElementById(`cell-${i}-${j}`);
            if (cell) {
              cell.textContent = grid[i][j];
            }
          }
        }
      }

      // Color word cells based on correctness
      function colorWordCells(word, row, quizResults) {
        console.log(`colorWordCells called: word="${word}", row=${row}, quizResults=`, quizResults);
        
        const targetLetters = targetWord.split('');
        const wordLetters = word.split('');
        const letterCounts = {};
        
        // Count letters in target word
        targetLetters.forEach(letter => {
          letterCounts[letter] = (letterCounts[letter] || 0) + 1;
        });
        
        // First pass: mark correct positions
        const cellStates = new Array(COLS).fill('absent');
        for (let i = 0; i < COLS; i++) {
          if (wordLetters[i] === targetLetters[i]) {
            cellStates[i] = 'correct';
            letterCounts[wordLetters[i]]--;
          }
        }
        
        // Second pass: mark present letters
        for (let i = 0; i < COLS; i++) {
          if (cellStates[i] === 'absent' && letterCounts[wordLetters[i]] > 0) {
            cellStates[i] = 'present';
            letterCounts[wordLetters[i]]--;
          }
        }
        
        // Apply colors to cells with animation
        for (let i = 0; i < COLS; i++) {
          const cellId = `cell-${row}-${i}`;
          const cell = document.getElementById(cellId);
          console.log(`Targeting cell ${cellId}, found:`, !!cell);
          if (cell) {
            setTimeout(() => {
              // Clear any existing classes first
              cell.classList.remove('correct', 'present', 'absent', 'incorrect');
              
              // Check if quiz was answered correctly
              if (quizResults && quizResults[i]) {
                // Quiz answered correctly - use normal Wordle colors and reveal position
                cell.classList.add(cellStates[i]);
                updateKeyboardColor(wordLetters[i], cellStates[i]);
              } else {
                // Quiz was wrong or skipped - mark as incorrect (red), don't reveal position
                // Don't update keyboard colors for incorrect answers
                cell.classList.add('incorrect');
              }
              
              cell.style.animationDelay = `${i * 0.1}s`;
              cell.classList.add('flip');
            }, i * 100);
          }
        }
      }

      // Update keyboard key colors
      function updateKeyboardColor(letter, state) {
        const keys = document.querySelectorAll('.keyboard-key');
        keys.forEach(key => {
          if (key.textContent === letter) {
            // Priority: correct > present > absent
            // Never downgrade from a higher priority state
            if (key.classList.contains('correct')) {
              return; // Already correct, don't change
            }
            if (key.classList.contains('present') && state === 'absent') {
              return; // Don't downgrade from present to absent
            }
            key.classList.remove('absent', 'present', 'correct', 'incorrect');
            key.classList.add(state);
          }
        });
      }

      // Get randomized question type with reduced repetition
      function getRandomizedQuestionType(questionType, aminoAcid, letterPosition) {
        if (questionType !== 'all') {
          return questionType; // Use selected type if not "all"
        }
        
        // Available question types for this amino acid
        const availableTypes = ['attributes', 'abbreviations'];
        if (aminoAcid["Image"]) {
          availableTypes.push('image');
        }
        
        // Filter out recently used types for this position to reduce repetition
        const recentTypesForPosition = questionHistory
          .filter(entry => entry.position === letterPosition)
          .slice(-3) // Last 3 questions for this position
          .map(entry => entry.type);
        
        // Prefer types that haven't been used recently
        const preferredTypes = availableTypes.filter(type => 
          !recentTypesForPosition.includes(type)
        );
        
        // Use preferred types if available, otherwise use any available type
        const typesToChooseFrom = preferredTypes.length > 0 ? preferredTypes : availableTypes;
        
        // Add some weighted randomization to prefer certain types
        const weights = {
          'attributes': 3, // Slightly prefer attributes (educational value)
          'abbreviations': 2,
          'image': 4 // Prefer images when available (visual learning)
        };
        
        // Create weighted array
        const weightedTypes = [];
        typesToChooseFrom.forEach(type => {
          const weight = weights[type] || 1;
          for (let i = 0; i < weight; i++) {
            weightedTypes.push(type);
          }
        });
        
        const selectedType = weightedTypes[Math.floor(Math.random() * weightedTypes.length)];
        
        // Record this question in history
        questionHistory.push({
          type: selectedType,
          position: letterPosition,
          timestamp: Date.now()
        });
        
        // Keep history size manageable
        if (questionHistory.length > MAX_HISTORY_SIZE) {
          questionHistory = questionHistory.slice(-MAX_HISTORY_SIZE);
        }
        
        return selectedType;
      }

      // Show quiz modal
      function showQuizModal(letter, position, callback) {
        // Quiz modal called for letter and position
        
        // Remove any existing modals first
        const existingModals = document.querySelectorAll('.quiz-modal');
        existingModals.forEach(modal => modal.remove());
        
        const baseQuestionType = questionTypeSelect ? questionTypeSelect.value : 'all';
        
        // In hard mode, pick a random amino acid instead of using the letter from the word
        let aminoAcid;
        if (difficultyMode === 'hard') {
          // Filter to amino acids with valid data
          const validAminoAcids = aminoleData.filter(aa => 
            aa["Full Name"] && aa["Full Name"] !== "None" && aa["Prompt Letter"]
          );
          if (validAminoAcids.length > 0) {
            aminoAcid = validAminoAcids[Math.floor(Math.random() * validAminoAcids.length)];
          }
        } else {
          // Normal mode: Find the amino acid that corresponds to this letter
          aminoAcid = aminoleData.find(amino => amino["Prompt Letter"] === letter);
        }
        
        if (!aminoAcid) {
          callback(true);
          return;
        }
        
        // Check if this amino acid has valid data
        if (!aminoAcid["Full Name"] || aminoAcid["Full Name"] === "None") {
          callback(true);
          return;
        }
        
        // Get randomized question type to reduce repetition
        const questionType = getRandomizedQuestionType(baseQuestionType, aminoAcid, position);
        
        let questionText = '';
        let correctAnswer = '';
        
        let isMultipleChoice = false;
        let choices = [];
        
        switch (questionType) {
          case 'image':
            if (aminoAcid["Image"]) {
              // Fix image path - convert from src path to public path
              let imagePath = aminoAcid["Image"];
              if (imagePath.startsWith('./src/assets/')) {
                imagePath = imagePath.replace('./src/assets/', '/assets/');
              }
              questionText = `<img src="${imagePath}" alt="Amino acid structure" style="max-width: 300px; max-height: 200px;"><br>What is the full name of this amino acid?`;
              correctAnswer = aminoAcid["Full Name"];
            } else {
              questionText = `What is the full name of the amino acid with 1-letter code "${letter}"?`;
              correctAnswer = aminoAcid["Full Name"];
            }
            break;
          case 'attributes':
            // Make attributes multiple choice
            isMultipleChoice = true;
            questionText = `What is a key attribute of ${aminoAcid["Full Name"]}?`;
            correctAnswer = aminoAcid["Key Attribute"];
            
            // Generate 3 incorrect options from other amino acids
            const otherAttributes = aminoleData
              .filter(aa => aa["Key Attribute"] && aa["Key Attribute"] !== "None" && aa["Key Attribute"] !== correctAnswer)
              .map(aa => aa["Key Attribute"]);
            
            // Remove duplicates, shuffle and take 3 random incorrect options
            const uniqueAttributes = [...new Set(otherAttributes)];
            const shuffledAttributes = uniqueAttributes.sort(() => 0.5 - Math.random());
            choices = [correctAnswer, ...shuffledAttributes.slice(0, 3)];
            
            // Shuffle the choices so correct answer isn't always first
            choices = choices.sort(() => 0.5 - Math.random());
            break;
          case 'abbreviations':
            questionText = `What is the 3-letter abbreviation for ${aminoAcid["Full Name"]}?`;
            correctAnswer = aminoAcid["3 Letter Abbreviation"];
            break;
          case 'all':
            // For "All Questions" mode, randomly select from available question types (excluding 1-letter questions)
            const allQuestionTypes = ['attributes', 'abbreviations'];
            if (aminoAcid["Image"]) {
              allQuestionTypes.push('image');
            }
            const randomType = allQuestionTypes[Math.floor(Math.random() * allQuestionTypes.length)];
            
            switch (randomType) {
              case 'image':
                // Fix image path - convert from src path to public path
                let allImagePath = aminoAcid["Image"];
                if (allImagePath.startsWith('./src/assets/')) {
                  allImagePath = allImagePath.replace('./src/assets/', '/assets/');
                }
                questionText = `<img src="${allImagePath}" alt="Amino acid structure" style="max-width: 300px; max-height: 200px;"><br>What is the full name of this amino acid?`;
                correctAnswer = aminoAcid["Full Name"];
                break;
              case 'attributes':
                // Make attributes multiple choice
                isMultipleChoice = true;
                questionText = `What is a key attribute of ${aminoAcid["Full Name"]}?`;
                correctAnswer = aminoAcid["Key Attribute"];
                
                // Generate 3 incorrect options from other amino acids
                const allOtherAttributes = aminoleData
                  .filter(aa => aa["Key Attribute"] && aa["Key Attribute"] !== "None" && aa["Key Attribute"] !== correctAnswer)
                  .map(aa => aa["Key Attribute"]);
                
                // Remove duplicates and shuffle
                const uniqueAttributes = [...new Set(allOtherAttributes)];
                const shuffledAllAttributes = uniqueAttributes.sort(() => 0.5 - Math.random());
                
                // Take 3 unique incorrect options
                const incorrectChoices = shuffledAllAttributes.slice(0, 3);
                choices = [correctAnswer, ...incorrectChoices];
                
                // Shuffle the choices so correct answer isn't always first
                choices = choices.sort(() => 0.5 - Math.random());
                break;
              case 'abbreviations':
                questionText = `What is the 3-letter abbreviation for ${aminoAcid["Full Name"]}?`;
                correctAnswer = aminoAcid["3 Letter Abbreviation"];
                break;
              default: // 'name'
                questionText = `What is the full name of the amino acid with 1-letter code "${letter}"?`;
                correctAnswer = aminoAcid["Full Name"];
                break;
            }
            break;
          default: // fallback to name question
            questionText = `What is the full name of the amino acid with 1-letter code "${letter}"?`;
            correctAnswer = aminoAcid["Full Name"];
            break;
        }
        
        // Ensure we have a valid answer
        if (!correctAnswer || correctAnswer.trim() === '') {
          correctAnswer = 'Unknown';
        }
        
        // Create modal
        const modal = document.createElement('div');
        modal.className = 'quiz-modal';
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        `;
        
        const modalContent = document.createElement('div');
        modalContent.className = 'quiz-content';
        modalContent.style.cssText = `
          background-color: white;
          padding: 20px;
          border-radius: 10px;
          text-align: center;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        `;
        
        let questionHTML = `
          <h3 style="margin-bottom: 20px; color: #333;">Quiz for letter "${letter}"</h3>
          <div class="question" style="margin-bottom: 20px; font-size: 16px;">${questionText}</div>
        `;
        
        if (isMultipleChoice) {
          // Multiple choice layout
          questionHTML += `<div class="choices" style="margin-bottom: 20px;">`;
          choices.forEach((choice, index) => {
            questionHTML += `
              <button class="choice-btn" data-choice="${choice}" style="
                display: block;
                width: 100%;
                padding: 10px;
                margin: 5px 0;
                border: 2px solid #ddd;
                border-radius: 5px;
                background-color: white;
                cursor: pointer;
                font-size: 14px;
                text-align: left;
              ">${choice}</button>
            `;
          });
          questionHTML += `</div>`;
          
          questionHTML += `
            <div class="quiz-buttons">
              <button id="submit-answer" style="
                background-color: #007bff;
                color: white;
                padding: 10px 20px;
                border: none;
                border-radius: 5px;
                margin: 0 10px;
                cursor: pointer;
                font-size: 14px;
                opacity: 0.5;
              " disabled>Submit</button>
              <button id="skip-question" style="
                background-color: #6c757d;
                color: white;
                padding: 10px 20px;
                border: none;
                border-radius: 5px;
                margin: 0 10px;
                cursor: pointer;
                font-size: 14px;
              ">Skip</button>
            </div>
          `;
        } else {
          // Text input layout
          questionHTML += `
            <input type="text" id="quiz-answer" placeholder="Enter your answer" style="
              width: 100%;
              padding: 10px;
              margin-bottom: 20px;
              border: 2px solid #ddd;
              border-radius: 5px;
              font-size: 16px;
              box-sizing: border-box;
            " />
            <div class="quiz-buttons">
              <button id="submit-answer" style="
                background-color: #007bff;
                color: white;
                padding: 10px 20px;
                border: none;
                border-radius: 5px;
                margin: 0 10px;
                cursor: pointer;
                font-size: 14px;
              ">Submit</button>
              <button id="skip-question" style="
                background-color: #6c757d;
                color: white;
                padding: 10px 20px;
                border: none;
                border-radius: 5px;
                margin: 0 10px;
                cursor: pointer;
                font-size: 14px;
              ">Skip</button>
            </div>
          `;
        }
        
        modalContent.innerHTML = questionHTML;
        
        modal.appendChild(modalContent);
        
        document.body.appendChild(modal);
        
        // Use setTimeout to ensure DOM elements are accessible
        setTimeout(() => {
          const answerInput = document.getElementById('quiz-answer');
          const submitBtn = document.getElementById('submit-answer');
          const skipBtn = document.getElementById('skip-question');
          const choiceBtns = document.querySelectorAll('.choice-btn');
          
          // DOM elements found and ready
          
          let selectedChoice = null;
          
          if (isMultipleChoice) {
            // Handle multiple choice selection
            choiceBtns.forEach(btn => {
              btn.addEventListener('click', () => {
                // Remove selection from all buttons
                choiceBtns.forEach(b => {
                  b.style.backgroundColor = 'white';
                  b.style.borderColor = '#ddd';
                });
                
                // Highlight selected button
                btn.style.backgroundColor = '#e3f2fd';
                btn.style.borderColor = '#2196f3';
                
                selectedChoice = btn.getAttribute('data-choice');
                
                // Enable submit button
                submitBtn.disabled = false;
                submitBtn.style.opacity = '1';
              });
            });
          } else {
            if (answerInput) {
              answerInput.focus();
            }
          }
          
          const showFeedback = (isCorrect, userAnswer) => {
            // Create feedback element
            const feedbackDiv = document.createElement('div');
            feedbackDiv.className = 'quiz-feedback';
            feedbackDiv.style.cssText = `
              margin-top: 20px;
              padding: 15px;
              border-radius: 8px;
              font-weight: bold;
              font-size: 16px;
              text-align: center;
              animation: fadeIn 0.3s ease-in;
            `;
            
            if (isCorrect) {
              feedbackDiv.style.backgroundColor = '#d4edda';
              feedbackDiv.style.color = '#155724';
              feedbackDiv.style.border = '2px solid #c3e6cb';
              // Create safe DOM elements instead of innerHTML
              const checkmark = document.createElement('div');
              checkmark.style.cssText = 'font-size: 24px; margin-bottom: 8px;';
              checkmark.textContent = '';
              
              const correctText = document.createElement('div');
              correctText.textContent = 'Correct!';
              
              const encouragement = document.createElement('div');
              encouragement.style.cssText = 'font-size: 14px; margin-top: 5px; font-weight: normal;';
              encouragement.textContent = 'Great job! You got it right.';
              
              feedbackDiv.appendChild(checkmark);
              feedbackDiv.appendChild(correctText);
              feedbackDiv.appendChild(encouragement);
            } else {
              feedbackDiv.style.backgroundColor = '#f8d7da';
              feedbackDiv.style.color = '#721c24';
              feedbackDiv.style.border = '2px solid #f5c6cb';
              // Create safe DOM elements instead of innerHTML
              const xmark = document.createElement('div');
              xmark.style.cssText = 'font-size: 24px; margin-bottom: 8px;';
              xmark.textContent = '';
              
              const incorrectText = document.createElement('div');
              incorrectText.textContent = 'Incorrect';
              
              const answerText = document.createElement('div');
              answerText.style.cssText = 'font-size: 14px; margin-top: 5px; font-weight: normal;';
              answerText.textContent = 'The correct answer was: ';
              
              const answerStrong = document.createElement('strong');
              answerStrong.textContent = correctAnswer; // Safe - no HTML injection
              answerText.appendChild(answerStrong);
              
              feedbackDiv.appendChild(xmark);
              feedbackDiv.appendChild(incorrectText);
              feedbackDiv.appendChild(answerText);
            }
            
            // Add CSS animation
            const style = document.createElement('style');
            style.textContent = `
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(-10px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `;
            document.head.appendChild(style);
            
            // Insert feedback before buttons
            const buttonsDiv = modalContent.querySelector('.quiz-buttons');
            modalContent.insertBefore(feedbackDiv, buttonsDiv);
            
            // Hide submit button and show continue button
            submitBtn.style.display = 'none';
            skipBtn.textContent = 'Continue';
            skipBtn.style.backgroundColor = '#28a745';
            
            // Disable input/choices
            if (isMultipleChoice) {
              const choiceBtns = modalContent.querySelectorAll('.choice-btn');
              choiceBtns.forEach(btn => {
                btn.disabled = true;
                btn.style.cursor = 'not-allowed';
                btn.style.opacity = '0.6';
                
                // Highlight correct/incorrect choices
                if (btn.getAttribute('data-choice').toLowerCase() === correctAnswer.toLowerCase()) {
                  btn.style.backgroundColor = '#d4edda';
                  btn.style.borderColor = '#28a745';
                  btn.style.color = '#155724';
                } else if (btn.getAttribute('data-choice') === userAnswer) {
                  btn.style.backgroundColor = '#f8d7da';
                  btn.style.borderColor = '#dc3545';
                  btn.style.color = '#721c24';
                }
              });
            } else {
              answerInput.disabled = true;
              answerInput.style.backgroundColor = '#f8f9fa';
              answerInput.style.cursor = 'not-allowed';
            }
          };
          
          // Show spelling check dialog for close matches
          const showSpellingCheckDialog = (userAnswer, correctAnswer, spellingResult, callback) => {
            // Create spelling check modal
            const spellingModal = document.createElement('div');
            spellingModal.className = 'spelling-check-modal';
            spellingModal.style.cssText = `
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.7);
              display: flex;
              justify-content: center;
              align-items: center;
              z-index: 1001;
            `;
            
            const spellingContent = document.createElement('div');
            spellingContent.className = 'spelling-content';
            spellingContent.style.cssText = `
              background-color: white;
              padding: 25px;
              border-radius: 15px;
              text-align: center;
              max-width: 450px;
              width: 90%;
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
            `;
            
            // Create elements safely without innerHTML to prevent XSS
            const iconDiv = document.createElement('div');
            iconDiv.style.cssText = 'font-size: 24px; margin-bottom: 15px; font-weight: bold; color: #666;';
            iconDiv.textContent = '?';
            
            const heading = document.createElement('h3');
            heading.style.cssText = 'margin: 0 0 15px 0; color: #333; font-size: 20px;';
            heading.textContent = 'Check Your Spelling';
            
            const para1 = document.createElement('p');
            para1.style.cssText = 'margin: 0 0 15px 0; color: #666; font-size: 16px; line-height: 1.4;';
            para1.textContent = 'Your answer "';
            const strong1 = document.createElement('strong');
            strong1.textContent = userAnswer;
            para1.appendChild(strong1);
            para1.appendChild(document.createTextNode('" is close to the correct answer.'));
            const br = document.createElement('br');
            para1.appendChild(br);
            const matchSpan = document.createElement('span');
            matchSpan.style.cssText = 'font-size: 14px; color: #888;';
            matchSpan.textContent = `(${spellingResult.similarity}% match)`;
            para1.appendChild(matchSpan);
            
            const para2 = document.createElement('p');
            para2.style.cssText = 'margin: 0 0 20px 0; color: #333; font-size: 16px;';
            para2.textContent = 'Did you mean "';
            const strong2 = document.createElement('strong');
            strong2.style.color = '#007bff';
            strong2.textContent = correctAnswer;
            para2.appendChild(strong2);
            para2.appendChild(document.createTextNode('"?'));
            
            const buttonsDiv = document.createElement('div');
            buttonsDiv.className = 'spelling-buttons';
            buttonsDiv.style.cssText = 'display: flex; gap: 10px; justify-content: center;';
            
            const acceptBtn = document.createElement('button');
            acceptBtn.id = 'accept-correction';
            acceptBtn.style.cssText = `
              background-color: #28a745;
              color: white;
              padding: 12px 20px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.2s ease;
            `;
            acceptBtn.textContent = 'Yes, Accept';
            
            const keepBtn = document.createElement('button');
            keepBtn.id = 'keep-original';
            keepBtn.style.cssText = `
              background-color: #6c757d;
              color: white;
              padding: 12px 20px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.2s ease;
            `;
            keepBtn.textContent = 'No, Keep Mine';
            
            buttonsDiv.appendChild(acceptBtn);
            buttonsDiv.appendChild(keepBtn);
            
            spellingContent.appendChild(iconDiv);
            spellingContent.appendChild(heading);
            spellingContent.appendChild(para1);
            spellingContent.appendChild(para2);
            spellingContent.appendChild(buttonsDiv);
            
            spellingModal.appendChild(spellingContent);
            document.body.appendChild(spellingModal);
            
            // Add event listeners (buttons already created above)
            
            acceptBtn.addEventListener('click', () => {
              document.body.removeChild(spellingModal);
              callback(correctAnswer); // Use the correct answer
            });
            
            keepBtn.addEventListener('click', () => {
              document.body.removeChild(spellingModal);
              callback(userAnswer); // Keep the user's original answer
            });
            
            // Add hover effects
            acceptBtn.addEventListener('mouseenter', () => {
              acceptBtn.style.backgroundColor = '#218838';
              acceptBtn.style.transform = 'translateY(-1px)';
            });
            acceptBtn.addEventListener('mouseleave', () => {
              acceptBtn.style.backgroundColor = '#28a745';
              acceptBtn.style.transform = 'translateY(0)';
            });
            
            keepBtn.addEventListener('mouseenter', () => {
              keepBtn.style.backgroundColor = '#5a6268';
              keepBtn.style.transform = 'translateY(-1px)';
            });
            keepBtn.addEventListener('mouseleave', () => {
              keepBtn.style.backgroundColor = '#6c757d';
              keepBtn.style.transform = 'translateY(0)';
            });
          };
          
          const handleSubmit = () => {
            let userAnswer;
            
            if (isMultipleChoice) {
              userAnswer = selectedChoice;
              if (!userAnswer) {
                alert('Please select an answer!');
                return;
              }
            } else {
              userAnswer = answerInput.value.trim();
              if (!userAnswer) {
                alert('Please enter an answer!');
                return;
              }
            }
            
            let isCorrect = false;
            let spellingResult = null;
            
            if (isMultipleChoice) {
              // For multiple choice, use exact matching
              isCorrect = correctAnswer && userAnswer.toLowerCase() === correctAnswer.toLowerCase();
            } else {
              // For text input, use fuzzy matching for spelling tolerance
              spellingResult = checkSpellingTolerance(userAnswer, correctAnswer);
              isCorrect = spellingResult.isExact || spellingResult.isClose;
              
              // If it's close but not exact, show spelling check dialog
              if (spellingResult.isClose && !spellingResult.isExact && spellingResult.similarity < 100) {
                showSpellingCheckDialog(userAnswer, correctAnswer, spellingResult, (finalAnswer) => {
                  const finalIsCorrect = finalAnswer.toLowerCase() === correctAnswer.toLowerCase();
                  
                  // Show feedback with final result
                  showFeedback(finalIsCorrect, finalAnswer);
                  
                  // Track quiz result for statistics
                  const category = getQuizCategory(questionType, aminoAcid);
                  trackQuizResult(category, finalIsCorrect);
                  
                  // Update skip button to close modal and call callback
                  skipBtn.onclick = () => {
                    if (document.body.contains(modal)) {
                      document.body.removeChild(modal);
                    }
                    callback(finalIsCorrect);
                  };
                });
                return; // Exit early, spelling check dialog will handle the rest
              }
            }
            
            // Show feedback immediately
            showFeedback(isCorrect, userAnswer);
            
            // Track quiz result for statistics
            const category = getQuizCategory(questionType, aminoAcid);
            trackQuizResult(category, isCorrect);
            
            // Update skip button to close modal and call callback
            skipBtn.onclick = () => {
              if (document.body.contains(modal)) {
                document.body.removeChild(modal);
              }
              callback(isCorrect);
            };
          };
          
          const handleSkip = () => {
            if (document.body.contains(modal)) {
              document.body.removeChild(modal);
            }
            callback(false);
          };
          
          submitBtn.addEventListener('click', handleSubmit);
          skipBtn.addEventListener('click', handleSkip);
          
          if (answerInput) {
            answerInput.addEventListener('keypress', (e) => {
              if (e.key === 'Enter') handleSubmit();
            });
          }
        }, 10);
      }

      // Check word
      async function checkWord() {
        // Prevent multiple simultaneous checks
        if (isCheckingWord) {
          console.log('Already checking a word, ignoring duplicate call');
          return;
        }
        isCheckingWord = true;
        
        console.log('🎯 ===== checkWord FUNCTION CALLED =====');
        const word = grid[currentRow].join('');
        console.log('📝 Word being checked:', word, 'on row:', currentRow);
        
        // Debug: Check if wordBank is loaded
        console.log('Word validation check:', {
          word: word,
          wordLower: word.toLowerCase(),
          wordBankLength: wordBank.length,
          wordBankSample: wordBank.slice(0, 5)
        });
        
        // Ensure wordBank is loaded
        if (wordBank.length === 0) {
          console.error('WordBank not loaded yet, attempting to reload...');
          await loadWordBank();
          if (wordBank.length === 0) {
            console.error('Failed to load wordBank, allowing word anyway');
            // Don't block the game if wordBank fails to load
          }
        }
        
        // Only validate if wordBank is loaded
        if (wordBank.length > 0 && !wordBank.includes(word.toLowerCase())) {
          alert('Not a valid word!');
          isCheckingWord = false; // Reset flag on early return
          return;
        }
        
        // Capture the current row BEFORE any async operations to prevent timing issues
        const wordRow = currentRow;
        console.log(`Captured wordRow=${wordRow} before quiz sequence`);
        
        // Show quiz for each letter
        const quizResults = [];
        
        console.log(`Starting quiz sequence for word: ${word} on row ${wordRow}`);
        for (let i = 0; i < COLS; i++) {
          const letter = word[i];
          console.log(`Loop iteration ${i}: Processing letter ${letter} at position ${i}`);
          await new Promise(resolve => {
            showQuizModal(letter, i, (isCorrect) => {
              quizResults[i] = isCorrect;
              console.log(`Quiz completed for letter ${letter} at position ${i}: ${isCorrect}`);
              resolve();
            });
          });
          console.log(`Finished processing letter ${letter} at position ${i}`);
        }
        console.log(`Quiz sequence completed. Results:`, quizResults);
        
        console.log(`Coloring cells for word "${word}" on row ${wordRow}`);
        
        // Color the cells based on quiz results
        colorWordCells(word, wordRow, quizResults);
        
        // Track stats after every completed word (row)
        const userId = getCurrentUserId();
        if (userId) {
          const sessionKey = `aminole_session_${userId}`;
          const session = JSON.parse(localStorage.getItem(sessionKey)) || {
            quizResults: [],
            categoryResults: {
              attributes: { correct: 0, total: 0 },
              abbreviations: { correct: 0, total: 0 },
              images: { correct: 0, total: 0 }
            }
          };

          const wordResult = {
            word: word,
            targetWord: targetWord,
            isCorrectWord: word === targetWord,
            rowNumber: wordRow + 1, // Use the captured row number
            quizAttempts: quizResults.length,
            quizCorrect: quizResults.filter(result => result).length,
            categoryResults: session.categoryResults,
            detailedAnswers: session.quizResults || [],
            timestamp: Date.now()
          };

          // Track this word attempt
          console.log('🎯 ===== TRACKING WORD COMPLETION =====');
          console.log('📊 Tracking word completion:', wordResult);
          await trackWordCompletion(wordResult);
          console.log('✅ Word completion tracking finished');
        }
        
        if (word === targetWord) {
          // Check if all quiz questions were answered correctly
          const allQuizCorrect = quizResults.every(result => result === true);
          
          if (allQuizCorrect) {
            // Game won - both word and all quiz questions correct
            if (userId) {
              const sessionKey = `aminole_session_${userId}`;
              const session = JSON.parse(localStorage.getItem(sessionKey)) || {
                quizResults: [],
                categoryResults: {
                  attributes: { correct: 0, total: 0 },
                  abbreviations: { correct: 0, total: 0 },
                  images: { correct: 0, total: 0 }
                }
              };

              const gameResult = {
                won: true,
                word: word,
                guesses: wordRow + 1, // Use the captured row number
                quizAttempts: quizResults.length,
                quizCorrect: quizResults.filter(result => result).length,
                categoryResults: session.categoryResults,
                aminoAcidsLearned: new Set(quizResults.map((_, index) => word[index])).size,
                detailedAnswers: session.quizResults || []
              };

              updateUserStats(gameResult);
              
              // Clear session data
              localStorage.removeItem(sessionKey);
            }

            // Show confetti and victory modal
            showConfettiAndVictoryModal(wordRow + 1);
            gameOver = true;
            
            // Mark daily word as completed and show freeplay option
            if (gameMode === 'daily') {
              markDailyWordCompleted();
              updateGameModeButtons();
            }
          } else {
            // Word correct but not all quiz questions - continue game
            const incorrectCount = quizResults.filter(result => result === false).length;
            alert(`Good job! You guessed the word "${targetWord}" but you got ${incorrectCount} quiz question(s) wrong. You need to get ALL questions correct to win!`);
            // Don't end the game, let them continue
          }
        } else if (currentRow === ROWS - 1) {
          // Game lost - collect final statistics
          if (userId) {
            const sessionKey = `aminole_session_${userId}`;
            const session = JSON.parse(localStorage.getItem(sessionKey)) || {
              quizResults: [],
              categoryResults: {
                attributes: { correct: 0, total: 0 },
                abbreviations: { correct: 0, total: 0 },
                images: { correct: 0, total: 0 }
              }
            };

            const gameResult = {
              won: false,
              word: word,
              guesses: ROWS,
              quizAttempts: quizResults.length,
              quizCorrect: quizResults.filter(result => result).length,
              categoryResults: session.categoryResults,
              aminoAcidsLearned: new Set(quizResults.map((_, index) => word[index])).size,
              detailedAnswers: session.quizResults || []
            };

            updateUserStats(gameResult);
            
            // Clear session data
            localStorage.removeItem(sessionKey);
          }

          gameOver = true;
          
          // Mark daily word as completed even if lost
          if (gameMode === 'daily') {
            markDailyWordCompleted();
            updateGameModeButtons();
          }
          
          setTimeout(() => {
            alert(`Game Over! The word was "${targetWord}".`);
          }, 1000);
        } else {
          currentRow++;
          currentCol = 0;
        }
        
        // Reset the checking flag at the end of word check
        isCheckingWord = false;
      }

      // Update game mode buttons based on daily word status
      function updateGameModeButtons() {
        if (dailyWordCompleted) {
          // Daily word completed - lock daily button, activate freeplay
          dailyModeBtn.style.display = 'inline-block';
          freeplayModeBtn.style.display = 'inline-block';
          dailyModeBtn.textContent = 'Daily Word (Completed ✓)';
          dailyModeBtn.disabled = true;
          dailyModeBtn.style.opacity = '0.5';
          dailyModeBtn.style.cursor = 'not-allowed';
          dailyModeBtn.classList.remove('active');
          dailyModeBtn.classList.add('completed');
          freeplayModeBtn.classList.add('active');
          freeplayModeBtn.textContent = 'Freeplay';
          
          // Force freeplay mode if daily is completed
          if (gameMode === 'daily') {
            gameMode = 'freeplay';
          }
        } else {
          // Daily word not completed - show daily as active, freeplay available
          dailyModeBtn.style.display = 'inline-block';
          freeplayModeBtn.style.display = 'inline-block';
          dailyModeBtn.textContent = 'Daily Word';
          dailyModeBtn.disabled = false;
          dailyModeBtn.style.opacity = '1';
          dailyModeBtn.style.cursor = 'pointer';
          dailyModeBtn.classList.remove('completed');
          freeplayModeBtn.textContent = 'Freeplay';
          
          if (gameMode === 'daily') {
            dailyModeBtn.classList.add('active');
            freeplayModeBtn.classList.remove('active');
          } else {
            dailyModeBtn.classList.remove('active');
            freeplayModeBtn.classList.add('active');
          }
        }
      }

      // Setup game mode buttons
      function setupGameModeButtons() {
        dailyModeBtn.addEventListener('click', async () => {
          if (!dailyWordCompleted && gameMode !== 'daily') {
            gameMode = 'daily';
            updateGameModeButtons();
            await resetGame();
          }
        });
        
        freeplayModeBtn.addEventListener('click', async () => {
          if (gameMode !== 'freeplay') {
            gameMode = 'freeplay';
            updateGameModeButtons();
            await resetGame();
          }
        });
        
        // Initial button setup
        updateGameModeButtons();
      }

      // Statistics tracking functions
      async function updateUserStats(gameResult) {
        const userId = getCurrentUserId();
        
        if (!userId) {
          return;
        }

        try {
          // Send game data to backend for teacher visibility
          await sendGameResultToBackend(gameResult);

          const statsKey = `aminole_stats_${userId}`;
          let stats = JSON.parse(localStorage.getItem(statsKey)) || {
            gamesPlayed: 0,
            gamesWon: 0,
            currentStreak: 0,
            maxStreak: 0,
            averageGuesses: 0,
            totalQuizCorrect: 0,
            totalQuizAttempts: 0,
            aminoAcidsLearned: 0,
            lastPlayedDate: null,
            guessDistribution: [0, 0, 0, 0, 0, 0],
            categoryStats: {
              attributes: { correct: 0, total: 0 },
              abbreviations: { correct: 0, total: 0 },
              images: { correct: 0, total: 0 }
            }
          };

          // Update game statistics
          stats.gamesPlayed++;
          stats.lastPlayedDate = new Date().toISOString();

          if (gameResult.won) {
            stats.gamesWon++;
            stats.currentStreak++;
            stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
            
            // Update guess distribution (guesses are 1-indexed)
            const guessIndex = gameResult.guesses - 1;
            if (guessIndex >= 0 && guessIndex < 6) {
              stats.guessDistribution[guessIndex]++;
            }
          } else {
            stats.currentStreak = 0;
          }

          // Update average guesses
          const totalGuesses = stats.guessDistribution.reduce((sum, count, index) => sum + (count * (index + 1)), 0);
          stats.averageGuesses = stats.gamesWon > 0 ? totalGuesses / stats.gamesWon : 0;

          // Update quiz statistics
          stats.totalQuizAttempts += gameResult.quizAttempts;
          stats.totalQuizCorrect += gameResult.quizCorrect;

          // Update category statistics
          if (gameResult.categoryResults) {
            Object.entries(gameResult.categoryResults).forEach(([category, result]) => {
              if (stats.categoryStats[category]) {
                stats.categoryStats[category].total += result.total;
                stats.categoryStats[category].correct += result.correct;
              }
            });
          }

          // Update amino acids learned (unique letters with correct quiz answers)
          if (gameResult.aminoAcidsLearned) {
            stats.aminoAcidsLearned = Math.max(stats.aminoAcidsLearned, gameResult.aminoAcidsLearned);
          }

          localStorage.setItem(statsKey, JSON.stringify(stats));
          console.log('Statistics updated:', stats);
        } catch (error) {
          console.error('Error updating user statistics:', error);
        }
      }

      // Send game result to backend for teacher visibility
      async function sendGameResultToBackend(gameResult) {
        try {
          // Check if user is authenticated
          const token = localStorage.getItem('aminole_token');
          const userStr = localStorage.getItem('aminole_user');
          
          if (!token || !userStr) {
            return;
          }

          // Check token expiry
          const tokenExpiry = localStorage.getItem('aminole_token_expiry');
          if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
            return;
          }

          // Get current user's class ID if they're in a class
          let classId = null;
          
          if (userStr) {
            const user = JSON.parse(userStr);
            classId = user.classId || null;
            
            // Check if user has student role
            if (user.role !== 'student') {
              return;
            }
          }

          // Also try to get classId from current class data
          const currentClassStr = localStorage.getItem('aminole_current_class');
          if (!classId && currentClassStr) {
            try {
              const currentClass = JSON.parse(currentClassStr);
              classId = currentClass.id || currentClass._id || null;
            } catch (e) {
              console.log('Could not parse current class data');
            }
          }

          // Calculate total time (estimate based on game duration)
          const estimatedTimePerQuestion = 30; // 30 seconds per question
          const totalTime = Math.round((gameResult.quizAttempts || 5) * estimatedTimePerQuestion);

          const gameData = {
            questionsAnswered: gameResult.quizAttempts || 5,
            correctAnswers: gameResult.quizCorrect || 0,
            totalTime: totalTime,
            classId: classId, // This might be null, which should be fine now
            gameType: 'wordle',
            difficulty: 'medium',
            answers: gameResult.detailedAnswers || []
          };
          
          
          const response = await apiCall('/progress/game', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(gameData),
          });

          if (response.ok) {
            await response.json();
          } else {
            // If authentication failed, clear tokens
            if (response.status === 401) {
              localStorage.removeItem('aminole_token');
              localStorage.removeItem('aminole_user');
              localStorage.removeItem('aminole_token_expiry');
            }
          }
        } catch (error) {
          console.error('Error sending game data to backend:', error);
        }
      }

      function getCurrentUserId() {
        try {
          const userStr = localStorage.getItem('aminole_user');
          if (userStr) {
            const user = JSON.parse(userStr);
            return user.id || user._id || user.userId;
          }
        } catch (error) {
          console.error('Error getting user ID:', error);
        }
        return null;
      }

      function trackQuizResult(category, isCorrect) {
        // Track individual quiz results for immediate feedback
        const userId = getCurrentUserId();
        if (!userId) return;

        try {
          const sessionKey = `aminole_session_${userId}`;
          let session = JSON.parse(localStorage.getItem(sessionKey)) || {
            quizResults: [],
            categoryResults: {
              attributes: { correct: 0, total: 0 },
              abbreviations: { correct: 0, total: 0 },
              images: { correct: 0, total: 0 }
            }
          };

          session.quizResults.push({ 
            category, 
            isCorrect, 
            timestamp: Date.now(),
            question: `Quiz for ${category}`,
            userAnswer: isCorrect ? 'Correct' : 'Incorrect',
            correctAnswer: 'N/A',
            timeSpent: 30 // Estimated time per question
          });
          
          if (session.categoryResults[category]) {
            session.categoryResults[category].total++;
            if (isCorrect) {
              session.categoryResults[category].correct++;
            }
          }

          localStorage.setItem(sessionKey, JSON.stringify(session));
        } catch (error) {
          console.error('Error tracking quiz result:', error);
        }
      }

      function getQuizCategory(questionType, aminoAcid) {
        switch (questionType) {
          case 'image':
            return 'images';
          case 'attributes':
            return 'attributes';
          case 'abbreviations':
            return 'abbreviations';
          default:
            return 'name';
        }
      }

      // Track individual word completion (called after each word attempt)
      async function trackWordCompletion(wordResult) {
        try {
          // Check if user is authenticated
          const token = localStorage.getItem('aminole_token');
          const userStr = localStorage.getItem('aminole_user');
          
          if (!token || !userStr) {
            console.log('User not authenticated, skipping word completion tracking');
            return;
          }

          // Check token expiry
          const tokenExpiry = localStorage.getItem('aminole_token_expiry');
          if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
            console.log('Token expired, skipping word completion tracking');
            return;
          }

          const user = JSON.parse(userStr);
          if (user.role !== 'student') {
            console.log('User is not a student, skipping word completion tracking');
            return;
          }

          // Get class ID if available
          let classId = user.classId || null;
          const currentClassStr = localStorage.getItem('aminole_current_class');
          if (!classId && currentClassStr) {
            try {
              const currentClass = JSON.parse(currentClassStr);
              classId = currentClass.id || currentClass._id || null;
            } catch (e) {
              console.log('Could not parse current class data');
            }
          }

          // Prepare word completion data for backend
          const wordCompletionData = {
            questionsAnswered: wordResult.quizAttempts,
            correctAnswers: wordResult.quizCorrect,
            totalTime: wordResult.quizAttempts * 30, // 30 seconds per question estimate
            classId: classId,
            gameType: 'wordle',
            difficulty: 'medium',
            answers: wordResult.detailedAnswers,
            wordAttempt: {
              word: wordResult.word,
              targetWord: wordResult.targetWord,
              isCorrect: wordResult.isCorrectWord,
              rowNumber: wordResult.rowNumber,
              timestamp: wordResult.timestamp
            }
          };

          console.log('🚀 ATTEMPTING TO SEND WORD COMPLETION DATA TO BACKEND');
          console.log('📊 Word completion data:', wordCompletionData);
          console.log('🔗 API URL:', process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
          console.log('🎯 Full endpoint: /progress/word-completion');
          
          const response = await apiCall('/progress/word-completion', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(wordCompletionData),
          });

          console.log('📡 Word completion response received:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok
          });

          if (response.ok) {
            const result = await response.json();
            console.log('✅ Word completion data sent successfully:', result);
          } else {
            // If the endpoint doesn't exist, fall back to the regular game endpoint
            if (response.status === 404) {
              console.log('Word completion endpoint not found, using regular game endpoint');
              const gameData = {
                questionsAnswered: wordResult.quizAttempts,
                correctAnswers: wordResult.quizCorrect,
                totalTime: wordResult.quizAttempts * 30,
                classId: classId,
                gameType: 'wordle',
                difficulty: 'medium',
                answers: wordResult.detailedAnswers
              };
              
              const fallbackResponse = await apiCall('/progress/game', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(gameData),
              });
              
              if (fallbackResponse.ok) {
                console.log('Word completion tracked via game endpoint');
              } else {
                console.error('Failed to track word completion:', fallbackResponse.status);
              }
            } else {
              const errorText = await response.text();
              console.error('Failed to send word completion data:', response.status, errorText);
            }
          }
        } catch (error) {
          console.error('Error tracking word completion:', error);
        }
      }

      // Confetti animation and victory modal function
      function showConfettiAndVictoryModal(guesses) {
        // Create confetti animation
        createConfetti();
        
        // Show victory modal after short delay
        setTimeout(() => {
          showVictoryModal(guesses);
        }, 1000);
      }

      function createConfetti() {
        const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'];
        const confettiCount = 100;
        
        for (let i = 0; i < confettiCount; i++) {
          const confetti = document.createElement('div');
          confetti.style.cssText = `
            position: fixed;
            width: 10px;
            height: 10px;
            background-color: ${colors[Math.floor(Math.random() * colors.length)]};
            left: ${Math.random() * 100}vw;
            top: -10px;
            z-index: 10000;
            pointer-events: none;
            animation: confetti-fall ${2 + Math.random() * 3}s linear forwards;
          `;
          
          document.body.appendChild(confetti);
          
          // Remove confetti after animation
          setTimeout(() => {
            if (confetti.parentNode) {
              confetti.parentNode.removeChild(confetti);
            }
          }, 5000);
        }
      }

      function showVictoryModal(guesses) {
        // Create modal backdrop
        const backdrop = document.createElement('div');
        backdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 10001;
          animation: fadeIn 0.3s ease-in;
        `;

        // Create modal content
        const modal = document.createElement('div');
        modal.style.cssText = `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 40px;
          border-radius: 20px;
          text-align: center;
          color: white;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          max-width: 400px;
          width: 90%;
          animation: modalSlideIn 0.5s ease-out;
        `;

        // Create elements safely without innerHTML to prevent XSS
        const heading = document.createElement('h2');
        heading.style.cssText = 'margin: 0 0 15px 0; font-size: 28px; font-weight: bold;';
        heading.textContent = 'Congratulations!';
        
        const para = document.createElement('p');
        para.style.cssText = 'margin: 0 0 20px 0; font-size: 18px; opacity: 0.9;';
        para.textContent = `You guessed the word "${targetWord}" in ${guesses} tries and got all quiz questions correct!`;
        
        const button = document.createElement('button');
        button.id = 'victory-close-btn';
        button.style.cssText = `
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid white;
          color: white;
          padding: 12px 30px;
          border-radius: 25px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
        `;
        button.textContent = 'Awesome!';
        
        modal.appendChild(heading);
        modal.appendChild(para);
        modal.appendChild(button);

        backdrop.appendChild(modal);
        document.body.appendChild(backdrop);

        // Close modal functionality
        const closeBtn = modal.querySelector('#victory-close-btn');
        const closeModal = () => {
          backdrop.style.animation = 'fadeOut 0.3s ease-out';
          setTimeout(() => {
            if (backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
            }
          }, 300);
        };

        closeBtn.addEventListener('click', closeModal);
        backdrop.addEventListener('click', (e) => {
          if (e.target === backdrop) closeModal();
        });

        // Hover effect for button
        closeBtn.addEventListener('mouseenter', () => {
          closeBtn.style.background = 'rgba(255, 255, 255, 0.3)';
          closeBtn.style.transform = 'translateY(-2px)';
        });
        closeBtn.addEventListener('mouseleave', () => {
          closeBtn.style.background = 'rgba(255, 255, 255, 0.2)';
          closeBtn.style.transform = 'translateY(0)';
        });
      }

      // Add CSS animations
      const style = document.createElement('style');
      style.textContent = `
        @keyframes confetti-fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);

      // Test backend connection
      async function testBackendConnection() {
        try {
          console.log('Testing backend connection...');
          const response = await apiCall('/health');
          if (response.ok) {
            const result = await response.json();
            console.log('Backend connection successful:', result);
          } else {
            console.error('Backend connection failed:', response.status);
          }
        } catch (error) {
          console.error('Backend connection error:', error);
        }
      }

      // Initialize game - check daily word status FIRST before loading word bank
      // This ensures the correct game mode is set before selecting the target word
      await checkDailyWordStatus();
      await loadWordBank();
      createGrid();
      createKeyboard();
      setupGameModeButtons();
      updateGameModeButtons();
      
      // Initialize mobile input for touchscreen devices
      if (isMobileDevice()) {
        initMobileInput();
        
        // Add tap handler to grid container for easy keyboard activation
        if (gridContainer) {
          gridContainer.addEventListener('touchstart', (e) => {
            // Don't interfere with cell-specific touches
            if (e.target.classList.contains('wordle-cell')) return;
            e.preventDefault();
            showMobileInput();
          });
        }
      }
      
      // Test backend connection on game load
      testBackendConnection();
      
      // Check user authentication status
      function checkAuthStatus() {
        const token = localStorage.getItem('aminole_token');
        const user = localStorage.getItem('aminole_user');
        const tokenExpiry = localStorage.getItem('aminole_token_expiry');
        
        // Only log in development mode and without sensitive data
        if (process.env.NODE_ENV === 'development') {
          console.log('Authentication Status Check:');
          console.log('- Token exists:', !!token);
          console.log('- User exists:', !!user);
          console.log('- Token expired:', tokenExpiry && Date.now() > parseInt(tokenExpiry));
          // Never log actual token, user data, or timestamps
        }
        
        // User data parsing for internal use only (no logging)
        if (user) {
          try {
            JSON.parse(user); // Validate user data format
            // User data processed internally without logging sensitive information
          } catch (e) {
            console.error('- Error parsing user data:', e);
          }
        }
      }
      
      checkAuthStatus();
      
      // Add manual test function to window for debugging
      window.testGameProgressLogging = async function() {
        console.log('=== MANUAL GAME PROGRESS TEST ===');
        
        const testGameResult = {
          won: true,
          word: 'AMINO',
          guesses: 3,
          quizAttempts: 5,
          quizCorrect: 4,
          categoryResults: {
            attributes: { correct: 2, total: 2 },
            abbreviations: { correct: 1, total: 2 },
            images: { correct: 1, total: 1 }
          },
          aminoAcidsLearned: 5,
          detailedAnswers: [
            { category: 'attributes', isCorrect: true, timestamp: Date.now() },
            { category: 'abbreviations', isCorrect: false, timestamp: Date.now() }
          ]
        };
        
        console.log('Test game result:', testGameResult);
        
        try {
          await sendGameResultToBackend(testGameResult);
          console.log('Manual test completed successfully');
        } catch (error) {
          console.error('Manual test failed:', error);
        }
      };

      // Add manual word completion test
      window.testWordCompletion = async function() {
        console.log('=== MANUAL WORD COMPLETION TEST ===');
        
        const testWordResult = {
          word: 'AMINO',
          targetWord: 'ACIDS',
          isCorrectWord: false,
          rowNumber: 2,
          quizAttempts: 5,
          quizCorrect: 3,
          categoryResults: {
            attributes: { correct: 1, total: 2 },
            abbreviations: { correct: 1, total: 2 },
            images: { correct: 1, total: 1 }
          },
          detailedAnswers: [
            { category: 'attributes', isCorrect: true, timestamp: Date.now() },
            { category: 'abbreviations', isCorrect: false, timestamp: Date.now() }
          ],
          timestamp: Date.now()
        };
        
        console.log('Test word result:', testWordResult);
        
        try {
          await trackWordCompletion(testWordResult);
          console.log('Manual word completion test completed successfully');
        } catch (error) {
          console.error('Manual word completion test failed:', error);
        }
      };
      
      // Add test function for cell coloring bug
      window.testCellColoring = function(testRow = 1) {
        console.log(`=== TESTING CELL COLORING ON ROW ${testRow} ===`);
        
        const testWord = 'AMINO';
        const testQuizResults = [true, false, true, true, false];
        
        console.log(`Testing colorWordCells with word="${testWord}", row=${testRow}`);
        colorWordCells(testWord, testRow, testQuizResults);
        
        // Check which cells got colored
        setTimeout(() => {
          for (let i = 0; i < 5; i++) {
            const cell = document.getElementById(`cell-${testRow}-${i}`);
            if (cell) {
              const classes = Array.from(cell.classList);
              console.log(`Cell ${testRow}-${i} classes:`, classes);
            } else {
              console.log(`Cell ${testRow}-${i} not found!`);
            }
          }
        }, 1000);
      };

      // Add comprehensive API test function
      window.testAPIConnection = async function() {
        console.log('=== TESTING API CONNECTION ===');
        
        // Test 1: Health check
        try {
          console.log('🏥 Testing health endpoint...');
          const healthResponse = await apiCall('/health');
          console.log('Health response:', healthResponse.status, healthResponse.statusText);
          if (healthResponse.ok) {
            const healthData = await healthResponse.json();
            console.log('✅ Health check passed:', healthData);
          } else {
            console.error('❌ Health check failed');
          }
        } catch (error) {
          console.error('❌ Health check error:', error);
        }

        // Test 2: Auth verification
        try {
          console.log('🔐 Testing auth verification...');
          const authResponse = await apiCall('/auth/verify');
          console.log('Auth response:', authResponse.status, authResponse.statusText);
          if (authResponse.ok) {
            const authData = await authResponse.json();
            console.log('✅ Auth verification passed:', authData);
          } else {
            const errorText = await authResponse.text();
            console.error('❌ Auth verification failed:', errorText);
          }
        } catch (error) {
          console.error('❌ Auth verification error:', error);
        }

        // Test 3: Manual game session creation
        try {
          console.log('🎮 Testing manual game session creation...');
          const testGameData = {
            questionsAnswered: 5,
            correctAnswers: 3,
            totalTime: 150,
            gameType: 'wordle',
            difficulty: 'medium',
            answers: [
              { question: 'Test Q1', userAnswer: 'Test A1', correctAnswer: 'Test A1', isCorrect: true, timeSpent: 30 },
              { question: 'Test Q2', userAnswer: 'Test A2', correctAnswer: 'Different', isCorrect: false, timeSpent: 30 }
            ]
          };

          const gameResponse = await apiCall('/progress/game', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(testGameData)
          });

          console.log('Game session response:', gameResponse.status, gameResponse.statusText);
          if (gameResponse.ok) {
            const gameResult = await gameResponse.json();
            console.log('✅ Game session created successfully:', gameResult);
          } else {
            const errorText = await gameResponse.text();
            console.error('❌ Game session creation failed:', errorText);
          }
        } catch (error) {
          console.error('❌ Game session creation error:', error);
        }
      };

      console.log('Manual test functions added:');
      console.log('- window.testGameProgressLogging() - Test full game completion');
      console.log('- window.testWordCompletion() - Test individual word completion');
      console.log('- window.testCellColoring(rowNumber) - Test cell coloring on specific row');
      console.log('- window.testAPIConnection() - Test API connectivity and endpoints');
      
      // Add keyboard event listener
      document.addEventListener('keydown', (e) => {
        // Check if we're actually on the game page by looking for the game container
        const gameContainer = document.querySelector('.wordle-container');
        if (!gameContainer) {
          return;
        }
        
        // Check if a quiz modal is open - if so, don't handle keyboard events for the game
        const modalOpen = document.querySelector('.quiz-modal');
        if (modalOpen) {
          return;
        }
        
        if (e.key === 'Backspace') {
          handleKeyPress('Backspace');
        } else if (e.key === 'Enter') {
          handleKeyPress('Enter');
        } else if (e.key.match(/[a-zA-Z]/)) {
          handleKeyPress(e.key.toUpperCase());
        }
      });
    };

    initializeGame();
  }, []);

  return (
    <div className="wordle-container">
      <div className="game-mode-selector">
        <button id="daily-mode" className="active">Daily Word</button>
        <button id="freeplay-mode">Freeplay</button>
      </div>
      <div className="question-selector">
        <label htmlFor="question-type">Question Type:</label>
        <select id="question-type" defaultValue="all">
          <option value="all">All Questions</option>
          <option value="image">Image Questions</option>
          <option value="attributes">Key Attributes</option>
          <option value="abbreviations">3-Letter Abbreviations</option>
        </select>
      </div>
      <div className="difficulty-selector">
        <label htmlFor="difficulty-mode">Difficulty:</label>
        <select id="difficulty-mode" defaultValue="normal">
          <option value="normal">Normal</option>
          <option value="hard">Hard (Random Questions)</option>
        </select>
      </div>
      <div id="wordle-grid"></div>
      <div id="keyboard"></div>
    </div>
  );
};

export default WordleGrid;
